import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { LayoutService } from '@app/shared/components/layout/layout.service';
import { ToastService } from '@teorema/patron';
import { utils } from 'src/app/shared';
import { environment } from 'src/environments/environment';

type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';
interface DiagnosticConsoleEntry {
  level: ConsoleLevel;
  args: any[];
  timestamp: string;
  stack?: string;
}

@Component({
  selector: 'app-diagnostic-summary',
  templateUrl: './diagnostic-summary.component.html',
  styleUrls: ['./diagnostic-summary.component.scss'],
  standalone: false,
})
export class DiagnosticSummaryComponent implements OnInit, OnDestroy, AfterViewInit {
  diagnosticData: any = null;
  diagnosticJson: string = '';
  previewLimit: number = 5;

  readonly environmentSummaryKeys = [
    'env',
    'system',
    'apiUrl',
    'apiPhp',
    'redirectPathAfterLogin',
    'title',
  ];

  // Chaves de storage que devem ser ignoradas (muito grandes ou sensíveis)
  private readonly storageKeysToIgnore = ['loglevel', 'debug', 'TEMPORARY', 'cache', 'temp'];

  // Prefixos de chaves de storage que devem ser ignoradas
  private readonly storageKeyPrefixesToIgnore = ['ng-', 'angular-', '_', 'tmp-'];

  // Tamanho máximo para valores de storage (em caracteres)
  private readonly maxStorageValueLength = 500;

  // Tamanho máximo para argumentos do console (em caracteres)
  private readonly maxConsoleArgLength = 300;

  // Flag para desabilitar captura do console (útil durante desenvolvimento)
  private readonly captureConsole = false; // Mude para true se quiser capturar logs

  private diagnosticModalRef: any;
  private consolePatched = false;
  private readonly maxConsoleEntries = 100;
  private consoleBuffer: DiagnosticConsoleEntry[] = [];
  private consoleOriginals: Partial<Record<ConsoleLevel, (...args: any[]) => void>> = {};
  private keydownHandler = (event: KeyboardEvent) => {
    if (event.ctrlKey && event.shiftKey && event.key?.toLowerCase() === 'd') {
      event.preventDefault();
      this.captureDiagnostic();
    }
  };

  @ViewChild('diagnosticModal') diagnosticModal?: TemplateRef<any>;

  constructor(
    private router: Router,
    private layoutService: LayoutService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.keydownHandler);
      this.setupConsoleCapture();
    }
  }

  ngAfterViewInit(): void {
    // no-op
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keydownHandler);
      this.restoreConsoleCapture();
    }
    if (this.diagnosticModalRef?.close) {
      this.diagnosticModalRef.close();
    }
  }

  captureDiagnostic() {
    const payload = this.collectDiagnosticData();
    this.diagnosticData = payload;
    this.diagnosticJson = JSON.stringify(payload, null, 2);
    if (this.diagnosticModal) {
      if (this.diagnosticModalRef?.close) {
        this.diagnosticModalRef.close();
      }
      this.diagnosticModalRef =
        this.layoutService.openGlobalModal({
          title: 'Diagnóstico da tela',
          size: 'xl',
          contentTemplate: this.diagnosticModal,
          showFooter: false,
          closeButton: true,
          scrollable: true,
          openDelay: 10,
        }) || this.diagnosticModalRef;
    }
  }

  async copyDiagnostic() {
    if (!this.diagnosticJson) return;

    const text = this.diagnosticJson;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        this.toastService.show({
          color: 'success',
          message: 'Diagnóstico copiado para a área de transferência',
        });
        return;
      } catch (error) {}
    }

    if (typeof document === 'undefined') {
      this.toastService.show({ color: 'warn', message: 'Não foi possível copiar automaticamente' });
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      const result = document.execCommand('copy');
      if (result) {
        this.toastService.show({
          color: 'success',
          message: 'Diagnóstico copiado para a área de transferência',
        });
      } else {
        this.toastService.show({
          color: 'warn',
          message: 'Não foi possível copiar automaticamente',
        });
      }
    } catch (error) {
      this.toastService.show({ color: 'warn', message: 'Não foi possível copiar automaticamente' });
    } finally {
      document.body.removeChild(textarea);
    }
  }

  downloadDiagnostic() {
    if (!this.diagnosticJson) return;
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      this.toastService.show({ color: 'warn', message: 'Não foi possível baixar automaticamente' });
      return;
    }

    const blob = new Blob([this.diagnosticJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const filename = `diagnostico-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);

    try {
      link.click();
      this.toastService.show({ color: 'success', message: 'Arquivo de diagnóstico baixado' });
    } catch (error) {
      this.toastService.show({ color: 'warn', message: 'Não foi possível baixar automaticamente' });
    } finally {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  get hasData(): boolean {
    return !!this.diagnosticData;
  }

  getEnvironmentSummary() {
    const env = this.diagnosticData?.envConfig;
    if (!env) {
      return [];
    }

    return this.environmentSummaryKeys
      .filter(key => env[key] !== undefined)
      .map(key => ({ key, value: this.formatValue(env[key]) }));
  }

  getStoragePreview(entries: { key: string; value: any }[] | undefined) {
    if (!entries || !entries.length) {
      return [];
    }
    return entries.slice(0, this.previewLimit);
  }

  hasStorageOverflow(entries: { key: string; value: any }[] | undefined) {
    if (!entries) {
      return false;
    }
    return entries.length > this.previewLimit;
  }

  getStorageOverflowCount(entries: { key: string; value: any }[] | undefined) {
    if (!entries) {
      return 0;
    }
    return Math.max(entries.length - this.previewLimit, 0);
  }

  getConsolePreview(entries: DiagnosticConsoleEntry[] | undefined) {
    if (!entries || !entries.length) {
      return [];
    }
    return entries.slice(-this.previewLimit).reverse();
  }

  getRouteBreadcrumb(route: any): string {
    if (!route) {
      return '/';
    }

    const segments: string[] = [];
    this.collectRouteSegments(route, segments);
    const filtered = segments.filter(segment => segment && segment !== '/');
    return filtered.length ? filtered.join(' / ') : '/';
  }

  formatValue(value: any, maxLength: number = 120) {
    if (value === undefined || value === null) {
      return '—';
    }

    let asString: string;
    if (typeof value === 'string') {
      asString = value;
    } else {
      try {
        asString = JSON.stringify(value);
      } catch (error) {
        asString = String(value);
      }
    }

    if (asString.length > maxLength) {
      return asString.slice(0, maxLength) + '…';
    }

    return asString || '—';
  }

  private collectDiagnosticData() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return {};
    }

    const rawUser = this.safeSerialize(utils.getUser());
    const user = this.filterUserData(rawUser);
    const route = this.extractRouteSnapshot(this.router.routerState.snapshot.root);
    const modals = this.getOpenModals();
    const toasts = this.getVisibleToasts();
    const navigatorInfo =
      typeof navigator === 'undefined'
        ? null
        : {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
          };

    const localStorageEntries = this.getStorageDump(
      typeof window !== 'undefined' ? window.localStorage : undefined
    );
    const sessionStorageEntries = this.getStorageDump(
      typeof window !== 'undefined' ? window.sessionStorage : undefined
    );
    const accessType = this.getAccessType(localStorageEntries);

    return {
      timestamp: new Date().toISOString(),
      environment: environment.env,
      system: environment.system,
      envConfig: this.getEnvironmentSnapshot(),
      url: window.location.href,
      documentTitle: document.title,
      route,
      user,
      modals,
      toasts,
      accessType,
      modalOpen: document.body.classList.contains('modal-open'),
      scrollPosition: {
        x: window.scrollX,
        y: window.scrollY,
      },
      screen: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
      },
      navigator: navigatorInfo,
      focusElement: this.getActiveElementInfo(),
      historyLength: window.history.length,
      localStorage: localStorageEntries,
      sessionStorage: sessionStorageEntries,
      layout: {
        headerNavCustomTemplate: !!this.layoutService.headerNavCustomTemplate,
      },
      console: {
        recent: this.consoleBuffer,
      },
    };
  }

  private filterUserData(user: any): any {
    if (!user || typeof user !== 'object') {
      return user;
    }

    // Propriedades relevantes do usuário
    const relevantKeys = [
      'id',
      'name',
      'email',
      'username',
      'cpf',
      'company_id',
      'company_name',
      'person_id',
      'user_type',
      'active',
      'roles',
      'permissions',
    ];

    const filtered: any = {};

    relevantKeys.forEach(key => {
      if (user[key] !== undefined) {
        // Limitar arrays muito grandes
        if (Array.isArray(user[key]) && user[key].length > 10) {
          filtered[key] = user[key]
            .slice(0, 10)
            .concat([`... mais ${user[key].length - 10} itens`]);
        } else {
          filtered[key] = user[key];
        }
      }
    });

    return Object.keys(filtered).length > 0 ? filtered : null;
  }

  private extractRouteSnapshot(snapshot: ActivatedRouteSnapshot | null, depth: number = 0): any {
    if (!snapshot) {
      return null;
    }

    // Limitar profundidade para evitar árvores muito grandes
    if (depth > 3) {
      return { truncated: true };
    }

    const children = snapshot.children
      ?.map(child => this.extractRouteSnapshot(child, depth + 1))
      .filter(child => child);

    return {
      routeConfigPath: snapshot.routeConfig?.path ?? null,
      url: snapshot.url.map(segment => segment.path),
      params: snapshot.params,
      queryParams: snapshot.queryParams,
      // Filtrar apenas dados relevantes do data
      data: this.filterRouteData(snapshot.data),
      component: snapshot.component ? ((snapshot.component as any).name ?? null) : null,
      children: children && children.length > 0 ? children : undefined,
    };
  }

  private filterRouteData(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    // Manter apenas propriedades relevantes
    const relevantKeys = ['title', 'breadcrumb', 'roles', 'permissions', 'requiresAuth'];
    const filtered: any = {};

    relevantKeys.forEach(key => {
      if (data[key] !== undefined) {
        filtered[key] = data[key];
      }
    });

    return Object.keys(filtered).length > 0 ? filtered : undefined;
  }

  private collectRouteSegments(route: any, segments: string[]) {
    if (!route) {
      return;
    }

    if (route.url && route.url.length) {
      const path = route.url.join('/');
      if (path) {
        segments.push(path);
      }
    } else if (route.routeConfigPath) {
      segments.push(route.routeConfigPath);
    }

    if (route.children && Array.isArray(route.children)) {
      route.children.forEach((child: any) => this.collectRouteSegments(child, segments));
    }
  }

  private getOpenModals() {
    if (typeof document === 'undefined') return [];

    return Array.from(document.querySelectorAll('.modal.show')).map(element => {
      const modal = element as HTMLElement;
      const titleElement = modal.querySelector(
        '.modal-title, .p-dialog-title, .modal-header h5, .modal-header .title, [data-modal-title]'
      );
      const title = titleElement?.textContent ? titleElement.textContent.trim() : null;
      return {
        id: modal.id || null,
        title,
        role: modal.getAttribute('role'),
        ariaLabel: modal.getAttribute('aria-label'),
      };
    });
  }

  private getVisibleToasts() {
    if (typeof document === 'undefined') return [];

    const selectors = [
      '.toast.show',
      '.p-toast-message',
      '.p-toast-item',
      '.p-toast-message-enter-done',
    ];

    const elements = selectors.flatMap(selector => Array.from(document.querySelectorAll(selector)));
    const uniqueElements = Array.from(new Set(elements)) as HTMLElement[];

    return uniqueElements
      .map(element => {
        const severity = element.getAttribute('data-severity') || element.className;
        const title = element.querySelector('.p-toast-summary, .toast-header, .toast-title');
        const content = element.querySelector('.p-toast-detail, .toast-body, .toast-message');
        return {
          severity: severity || null,
          title: title ? title.textContent?.trim() : null,
          message: content ? content.textContent?.trim() : element.textContent?.trim(),
        };
      })
      .filter(entry => entry.message);
  }

  private getActiveElementInfo() {
    if (typeof document === 'undefined') return null;
    const element = document.activeElement as HTMLElement | null;
    if (!element) return null;

    return {
      tag: element.tagName,
      id: element.id || null,
      name: element.getAttribute('name'),
      type: element.getAttribute('type'),
      role: element.getAttribute('role'),
    };
  }

  private getStorageDump(storage: Storage | undefined) {
    if (!storage) return [];
    const entries: { key: string; value: any }[] = [];
    try {
      for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index);
        if (!key) continue;

        // Filtrar chaves desnecessárias
        if (this.shouldIgnoreStorageKey(key)) {
          continue;
        }

        let rawValue: string | null = null;
        try {
          rawValue = storage.getItem(key);
        } catch (error) {}

        // Limitar tamanho do valor
        const value = this.safeSerializeStorageValue(rawValue);
        const truncatedValue = this.truncateStorageValue(value);

        entries.push({
          key,
          value: truncatedValue,
        });
      }
    } catch (error) {}
    return entries;
  }

  private shouldIgnoreStorageKey(key: string): boolean {
    // Verificar chaves exatas
    if (
      this.storageKeysToIgnore.some(ignored => key.toLowerCase().includes(ignored.toLowerCase()))
    ) {
      return true;
    }

    // Verificar prefixos
    if (this.storageKeyPrefixesToIgnore.some(prefix => key.startsWith(prefix))) {
      return true;
    }

    return false;
  }

  private truncateStorageValue(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'string') {
      if (value.length > this.maxStorageValueLength) {
        return value.substring(0, this.maxStorageValueLength) + '... [truncado]';
      }
      return value;
    }

    if (typeof value === 'object') {
      const stringified = JSON.stringify(value);
      if (stringified.length > this.maxStorageValueLength) {
        return (
          JSON.stringify(value, null, 0).substring(0, this.maxStorageValueLength) + '... [truncado]'
        );
      }
    }

    return value;
  }

  private safeSerializeStorageValue(value: string | null) {
    if (value === null) return null;
    try {
      return this.safeSerialize(JSON.parse(value));
    } catch (error) {
      return value;
    }
  }

  private getAccessType(entries: { key: string; value: any }[]) {
    if (!entries || !entries.length) {
      return null;
    }
    const match = entries.find(entry => entry.key === 'PatronAccessType');
    if (!match) {
      return null;
    }
    return typeof match.value === 'string' ? match.value : this.formatValue(match.value);
  }

  private setupConsoleCapture() {
    // Se captura de console está desabilitada, não fazer nada
    if (!this.captureConsole) return;

    if (this.consolePatched) return;
    const levels: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
    levels.forEach(level => {
      const original = console[level] as (...args: any[]) => void;
      if (typeof original !== 'function') {
        return;
      }
      this.consoleOriginals[level] = original;

      // Usar bind para criar uma função que não adiciona frame ao stack
      const boundOriginal = original.bind(console);
      const recordEntry = this.recordConsoleEntry.bind(this);

      console[level] = function (...args: any[]) {
        // Executar em paralelo usando setTimeout para não aparecer no stack
        setTimeout(() => recordEntry(level, args), 0);
        // Chamar o original diretamente
        return boundOriginal(...args);
      };
    });
    this.consolePatched = true;
  }

  private restoreConsoleCapture() {
    if (!this.consolePatched) return;
    const levels: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
    levels.forEach(level => {
      const original = this.consoleOriginals[level];
      if (original) {
        console[level] = original;
      }
    });
    this.consolePatched = false;
    this.consoleOriginals = {};
  }

  private recordConsoleEntry(level: ConsoleLevel, args: any[]) {
    const entry: DiagnosticConsoleEntry = {
      level,
      args: args.map(arg => this.normalizeConsoleArg(arg)),
      timestamp: new Date().toISOString(),
      stack: level === 'error' || level === 'warn' ? this.getStackTrace() : undefined,
    };
    this.consoleBuffer.push(entry);
    if (this.consoleBuffer.length > this.maxConsoleEntries) {
      this.consoleBuffer.splice(0, this.consoleBuffer.length - this.maxConsoleEntries);
    }
  }

  private getStackTrace() {
    try {
      const error = new Error();
      if (error.stack) {
        return error.stack.split('\n').slice(2).join('\n');
      }
    } catch (error) {}
    return undefined;
  }

  private normalizeConsoleArg(value: any) {
    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack ? value.stack.split('\n').slice(0, 5).join('\n') : undefined,
      };
    }

    const serialized = this.safeSerialize(value);

    // Limitar tamanho de strings
    if (typeof serialized === 'string' && serialized.length > this.maxConsoleArgLength) {
      return serialized.substring(0, this.maxConsoleArgLength) + '... [truncado]';
    }

    // Limitar tamanho de objetos
    if (typeof serialized === 'object' && serialized !== null) {
      const stringified = JSON.stringify(serialized);
      if (stringified.length > this.maxConsoleArgLength) {
        // Não tentar fazer parse de JSON truncado, apenas retornar indicação de truncamento
        return '[objeto truncado - tamanho: ' + stringified.length + ' caracteres]';
      }
    }

    return serialized;
  }

  private getEnvironmentSnapshot() {
    const env = this.safeSerialize(environment);

    // Filtrar apenas propriedades relevantes
    const relevantKeys = [
      'env',
      'system',
      'apiUrl',
      'apiPhp',
      'redirectPathAfterLogin',
      'title',
      'production',
      'version',
    ];

    const filtered: any = {};
    relevantKeys.forEach(key => {
      if (env && env[key] !== undefined) {
        filtered[key] = env[key];
      }
    });

    return filtered;
  }

  private safeSerialize(value: any) {
    if (value === undefined || value === null) {
      return null;
    }

    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      const partial: any = {};
      ['id', 'name', 'email', 'username', 'message', 'code'].forEach(key => {
        if (value[key] !== undefined) {
          partial[key] = value[key];
        }
      });
      return Object.keys(partial).length ? partial : value;
    }
  }
}
